import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import { atwMarkIdForSystem } from "@/components/Brand/atwProductMarks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import {
  buildSystemAccessDraft,
  visibleSystems,
} from "@/components/Directory/SystemAccessPanel";
import { directoryRoleLabel } from "@/components/Directory/roleLabels";
import { directoryHref } from "@/lib/atwDirectoryHost";
import {
  ATW_SYSTEMS,
  atwSystemLaunchUrl,
  type AtwSystemId,
} from "@/lib/atwSystemAccess";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";

type MyAccessRequest = {
  id: string;
  systemId: string;
  status: "pending" | "approved" | "denied";
  createdAt: string;
  message?: string | null;
};

export function HomePage() {
  const state = useDirectoryState();
  const [requestSystem, setRequestSystem] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [requestBusy, setRequestBusy] = useState(false);
  const [myRequests, setMyRequests] = useState<MyAccessRequest[]>([]);

  const ready = state.status === "ready" ? state.value : null;
  const me = ready?.me;
  const org = ready?.org;
  const canManageUsers = ready?.canManageUsers ?? false;
  const assignableAppSlugs = ready?.assignableAppSlugs ?? ([] as Exclude<GlobalAppSlug, "home">[]);

  const draft = useMemo(() => {
    if (!me) return null;
    return buildSystemAccessDraft({
      role: me.role,
      appEntitlements: me.appEntitlements,
      systemAccess: me.systemAccess,
      orgId: org?.orgId,
      orgMailEnabled: org?.mailEnabled === true,
    });
  }, [me, org?.orgId, org?.mailEnabled]);

  const granted = useMemo(() => {
    if (!draft) return [];
    const systems = visibleSystems(assignableAppSlugs);
    return systems.filter((system) => {
      if (system.storage.kind === "systemAccess") {
        return draft.systemAccess.includes(system.id as (typeof draft.systemAccess)[number]);
      }
      return draft.apps.includes(system.storage.slug as Exclude<GlobalAppSlug, "home">);
    });
  }, [draft, assignableAppSlugs]);

  const requestable = useMemo(() => {
    const grantedIds = new Set(granted.map((s) => s.id));
    return ATW_SYSTEMS.filter((s) => {
      if (grantedIds.has(s.id)) return false;
      if (
        s.storage.kind === "appEntitlement" &&
        s.storage.slug !== "home" &&
        !assignableAppSlugs.includes(s.storage.slug)
      ) {
        return false;
      }
      return Boolean(atwSystemLaunchUrl(s.id)) || s.id === "worksuite" || s.id === "platform";
    });
  }, [granted, assignableAppSlugs]);

  useEffect(() => {
    if (!ready) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/directory/access-request?mine=1&status=all"), {
          ...apiFetchInit(),
          cache: "no-store",
        });
        if (!res.ok || cancel) return;
        const data = await res.json();
        if (!cancel) setMyRequests((data.requests || []) as MyAccessRequest[]);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [ready]);

  if (!ready || !me || !draft) return null;

  const myPending = myRequests.filter((r) => r.status === "pending");

  const submitRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!requestSystem) {
      toast.error("Choose a system to request.");
      return;
    }
    setRequestBusy(true);
    try {
      const res = await fetch(apiUrl("/api/directory/access-request"), {
        ...apiFetchInit(),
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: requestSystem,
          message: requestMessage.trim() || undefined,
        }),
      });
      const data = (await res.json()) as { error?: string; note?: string; delivered?: boolean };
      if (!res.ok) {
        toast.error(data.error || "Could not send request.");
        return;
      }
      toast.success(
        data.delivered === false
          ? data.note || "Request recorded."
          : "Access request sent to your organization admins.",
      );
      setRequestMessage("");
      setRequestSystem("");
      const mine = await fetch(apiUrl("/api/directory/access-request?mine=1&status=all"), {
        ...apiFetchInit(),
        cache: "no-store",
      });
      if (mine.ok) {
        const mineData = await mine.json();
        setMyRequests((mineData.requests || []) as MyAccessRequest[]);
      }
    } catch {
      toast.error("Could not send request.");
    } finally {
      setRequestBusy(false);
    }
  };

  const systemName = (id: string) => ATW_SYSTEMS.find((s) => s.id === id)?.name || id;

  return (
    <div className="space-y-8">
      <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-[#1D232D]">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Dashboard
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
          {me.name || me.email}
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {org?.companyName || org?.domain || "Your organization"} ·{" "}
          <Badge variant="secondary" className="align-middle">
            {directoryRoleLabel(me.role)}
          </Badge>
        </p>
        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Sign-in email</dt>
            <dd className="text-gray-900 dark:text-white">{me.email}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Job title</dt>
            <dd className="text-gray-900 dark:text-white">{me.jobTitle || "—"}</dd>
          </div>
        </dl>
        <div className="mt-5 flex flex-wrap gap-2">
          {canManageUsers ? (
            <Link
              to={directoryHref("/people")}
              className="inline-flex h-9 items-center rounded-md bg-gray-900 px-3 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900"
            >
              Manage people
            </Link>
          ) : null}
          <Link
            to={directoryHref("/access")}
            className="inline-flex h-9 items-center rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
          >
            View my access
          </Link>
        </div>
      </section>

      <section>
        <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Open a system</h2>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Systems your organization has granted you. Sign-in is shared through ATW Auth.
        </p>
        {granted.length === 0 ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
            You do not have any systems yet. Request access below or ask an organization admin.
          </p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {granted.map((system) => {
              const href = atwSystemLaunchUrl(system.id as AtwSystemId);
              const markId = atwMarkIdForSystem(system.id);
              return (
                <li
                  key={system.id}
                  className="flex flex-col justify-between rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-[#1D232D]"
                >
                  <div className="flex items-start gap-3">
                    {markId ? (
                      <AtwProductMark
                        id={markId}
                        size={40}
                        className="dark:brightness-0 dark:invert"
                        label
                      />
                    ) : null}
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{system.name}</p>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{system.summary}</p>
                    </div>
                  </div>
                  {href ? (
                    <a
                      href={href}
                      className="mt-4 inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-gray-900 px-3 text-sm font-semibold text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900"
                    >
                      Open
                      <ArrowUpRight className="size-4" aria-hidden />
                    </a>
                  ) : (
                    <p className="mt-4 text-xs text-gray-500">Opens inside ATW One when available.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {myPending.length > 0 ? (
        <section className="rounded-xl border border-amber-200 bg-amber-50/80 p-6 dark:border-amber-900/40 dark:bg-amber-950/30">
          <h2 className="text-lg font-semibold text-amber-950 dark:text-amber-100">
            Your pending requests
          </h2>
          <p className="mt-1 text-sm text-amber-900 dark:text-amber-200">
            Waiting on an organization admin. You will get an email when they decide.
          </p>
          <ul className="mt-4 space-y-2">
            {myPending.map((req) => {
              const markId = atwMarkIdForSystem(req.systemId);
              return (
                <li
                  key={req.id}
                  className="flex items-center gap-3 text-sm text-amber-950 dark:text-amber-100"
                >
                  {markId ? (
                    <AtwProductMark id={markId} size={22} className="dark:brightness-0 dark:invert" />
                  ) : null}
                  <span className="font-medium">{systemName(req.systemId)}</span>
                  <Badge variant="outline">Pending</Badge>
                  <span className="text-xs text-amber-800/80 dark:text-amber-200/80">
                    {new Date(req.createdAt).toLocaleDateString()}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {requestable.length > 0 ? (
        <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-[#1D232D]">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Request access</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Ask your organization admin to turn on another ATW system for you.
          </p>
          <form className="mt-4 space-y-3" onSubmit={(e) => void submitRequest(e)}>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600 dark:text-gray-400">System</span>
              <select
                value={requestSystem}
                onChange={(e) => setRequestSystem(e.target.value)}
                className="h-10 w-full max-w-md rounded-md border border-gray-300 bg-white px-3 text-sm dark:border-white/20 dark:bg-[#12161d] dark:text-white"
                required
              >
                <option value="">Select…</option>
                {requestable.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600 dark:text-gray-400">Note (optional)</span>
              <textarea
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Why you need this system"
                className="w-full max-w-lg rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-white/20 dark:bg-[#12161d] dark:text-white"
              />
            </label>
            <Button type="submit" disabled={requestBusy || !requestSystem}>
              {requestBusy ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Sending…
                </>
              ) : (
                "Send request"
              )}
            </Button>
          </form>
        </section>
      ) : null}
    </div>
  );
}
